// Tiny eager Sentry buffer (~1 KB).
// Holds error reports until the real `@sentry/react` SDK loads
// post-paint via dynamic import — see src/sentry/init.ts.

type Captured =
  | { kind: 'exception'; error: unknown; tags?: Record<string, string>; ctx?: Record<string, unknown>; level?: string; fingerprint?: string[] }
  | { kind: 'message'; message: string; tags?: Record<string, string>; ctx?: Record<string, unknown>; level?: string; fingerprint?: string[] }
type ScopeCb = (scope: ScopeShim) => void

// Extra scope applied to a single capture (used by captureMessage so the
// support reference / user comment can ride along without a withScope dance).
export type CaptureOpts = {
  level?: string
  tags?: Record<string, string>
  ctx?: Record<string, unknown>
  fingerprint?: string[]
}

type ScopeShim = {
  setLevel: (level: string) => ScopeShim
  setTag: (k: string, v: string) => ScopeShim
  setContext: (k: string, v: Record<string, unknown>) => ScopeShim
  setFingerprint: (parts: string[]) => ScopeShim
}

const buffer: Captured[] = []

let realCaptureException: (error: unknown) => void = (e) => {
  buffer.push({ kind: 'exception', error: e })
}
let realWithScope: (cb: ScopeCb) => void = (cb) => {
  // Build an inert scope that just records what was set, attached to the
  // next captureException so the real SDK can replay it after binding.
  let level: string | undefined
  let tags: Record<string, string> = {}
  let ctx: Record<string, unknown> = {}
  let fingerprint: string[] | undefined
  const scope: ScopeShim = {
    setLevel: (l) => { level = l; return scope },
    setTag: (k, v) => { tags[k] = v; return scope },
    setContext: (k, v) => { ctx[k] = v; return scope },
    setFingerprint: (parts) => { fingerprint = parts; return scope },
  }
  // Patch one-shot: next captureException inside cb gets the buffered scope
  const prev = realCaptureException
  realCaptureException = (e) => {
    buffer.push({ kind: 'exception', error: e, tags, ctx, level, fingerprint })
    realCaptureException = prev
  }
  try { cb(scope) } finally {
    if (realCaptureException !== prev) realCaptureException = prev
  }
}
let realCaptureMessage: (message: string, opts?: CaptureOpts) => void = (
  message,
  opts,
) => {
  buffer.push({
    kind: 'message',
    message,
    level: opts?.level,
    tags: opts?.tags,
    ctx: opts?.ctx,
    fingerprint: opts?.fingerprint,
  })
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let realGetClient: () => any = () => undefined
// Pre-bind no-op: setTag only matters once the real SDK (and its scope) exist.
// The only caller — the error screen — sets it right before opening the
// native Feedback dialog, which itself needs the loaded SDK.
let realSetTag: (key: string, value: string | undefined) => void = () => {}

export function captureException(error: unknown): void {
  realCaptureException(error)
}
export function captureMessage(message: string, opts?: CaptureOpts): void {
  realCaptureMessage(message, opts)
}
export function withScope(cb: ScopeCb): void {
  realWithScope(cb)
}
export function setTag(key: string, value: string | undefined): void {
  realSetTag(key, value)
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getClient(): any {
  return realGetClient()
}

// Apply buffered scope data onto a real Sentry scope.
function applyScope(
  scope: unknown,
  item: { tags?: Record<string, string>; ctx?: Record<string, unknown>; level?: string; fingerprint?: string[] },
): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = scope as any
  if (item.level) s.setLevel(item.level)
  if (item.tags) for (const [k, v] of Object.entries(item.tags)) s.setTag(k, v)
  if (item.ctx) for (const [k, v] of Object.entries(item.ctx)) s.setContext(k, v)
  if (item.fingerprint) s.setFingerprint(item.fingerprint)
}

// Called by src/sentry/init.ts after the real SDK is loaded + initialised.
export function bindRealSentry(api: {
  captureException: (error: unknown) => void
  captureMessage: (message: string) => void
  withScope: (cb: (scope: unknown) => void) => void
  getClient: () => unknown
  setTag: (key: string, value: string | undefined) => void
}): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  realCaptureException = api.captureException as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  realWithScope = api.withScope as any
  realGetClient = api.getClient
  realSetTag = api.setTag
  realCaptureMessage = (message, opts) => {
    api.withScope((scope) => {
      if (opts) applyScope(scope, opts)
      api.captureMessage(message)
    })
  }
  // Drain buffer
  for (const item of buffer) {
    if (item.kind === 'exception') {
      api.withScope((scope) => {
        applyScope(scope, item)
        api.captureException(item.error)
      })
    } else if (item.kind === 'message') {
      api.withScope((scope) => {
        applyScope(scope, item)
        api.captureMessage(item.message)
      })
    }
  }
  buffer.length = 0
}

// Intentional zero-cost compat for the existing `SeverityLevel` type alias.
export type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
